import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const HomeLoanEMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal * monthlyInterest * Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: (currentMonth + i) % 12 + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === '' ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === '' ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === '' ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        label: 'EMI Breakdown',
        data: [loanAmount, totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
  const categories = [
    { id: 1, name: 'Category 1', link: '/' },
    { id: 2, name: 'Category 2', link: '/' },
    { id: 3, name: 'Category 3', link: '/' },
    { id: 4, name: 'Category 4', link: '/' },
    // Add more categories as needed
  ];
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is the EMI on a ₹50 lakh home loan for 20 years at 9% interest?',
        answer: 'Approx. ₹44,986. Total repayment: ₹1.08 crore.',
      },
      {
        question: 'Does the EMI remain the same throughout the tenure?',
        answer: 'Yes, for fixed-rate loans. In floating rate loans, the EMI may change based on rate revisions.',
      },
      {
        question: 'How can I reduce my home loan EMI?',
        answer: 'By increasing tenure, negotiating for lower interest rates, or making a bigger down payment.',
      },
      {
        question: 'Can I prepay my home loan?',
        answer: 'Yes, most lenders allow part or full prepayment, sometimes with minimal or no penalty.',
      },
      {
        question: 'What happens if I miss an EMI payment?',
        answer: 'Missing EMIs can attract penalties and impact your credit score.',
      },
      {
        question: 'How accurate is the EMI calculator?',
        answer: 'Our calculator uses a standard formula, ensuring precise results. However, actual values may vary slightly due to lender policies.',
      },
      {
        question: 'Can I use this calculator for under-construction property loans?',
        answer: 'Yes. Just enter your expected loan amount, rate, and tenure.',
      },
      {
        question: 'What is a floating interest rate?',
        answer: 'A rate that fluctuates with market conditions. Your EMI can go up or down over time.',
      },
      {
        question: 'Is it better to choose a longer or shorter tenure?',
        answer: 'Longer tenure = lower EMI but higher interest paid. Choose based on your income flow and goals.',
      },
      {
        question: 'Does the calculator show prepayment impact?',
        answer: 'The basic version doesn’t, but we provide resources and tools for that separately.',
      },
      {
        question: 'Can I include processing fees in the EMI?',
        answer: 'Processing fees are generally paid upfront and not included in EMI, but we help you factor them in total cost.',
      },
      {
        question: 'Should I opt for a joint home loan?',
        answer: 'Joint loans increase eligibility and offer tax benefits. Use our calculator to split EMI responsibilities.',
      },
      {
        question: 'What is the minimum home loan amount I can take?',
        answer: 'It depends on the bank, but typically starts from ₹5 lakh.',
      },
      {
        question: 'Can I use this tool for home renovation loans?',
        answer: 'Yes. The calculator works for all home-related secured loans.',
      },
      {
        question: 'Does it include tax benefit calculations?',
        answer: 'Not directly. But we offer separate tax tools to help you estimate savings under Sections 80C and 24(b).',
      },
    ];
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
    const calculators = [
      {
        name: 'SBI Home Loan Calculator',
        path: '/financial-tools-calculators/sbi-home-loan-emi-calculator',
        image: '/assets/hd.png',
      },
      {
        name: 'HDFC Home Loan Calculator',
        path: '/financial-tools-calculators/hdfc-home-loan-emi-calculator',
        image: '/assets/hd.png',
      },
      {
        name: 'ICICI Home Loan Calculator',
        path: '/financial-tools-calculators/icici-home-loan-emi-calculator',
        image: '/assets/hd.png',
      },
      {
        name: 'Axis Home Loan Calculator',
        path: '/financial-tools-calculators/axis-home-loan-emi-calculator',
        image: '/assets/hd.png',
      },
      {
        name: 'LIC Housing Finance Home Loan Calculator',
        path: '/financial-tools-calculators/lic-housing-finance-home-loan-emi-calculator',
        image: '/assets/hd.png',
      },
      {
        name: 'BOB Home Loan Calculator',
        path: '/financial-tools-calculators/bob-home-loan-emi-calculator',
        image: '/assets/hd.png',
      },
    ];
  return (
    <>

<Helmet>
         <title>EMI Calculator for Home Loan, Car Loan &amp; Personal Loan in India | TheFinmaster</title>
        <meta  name='description' content='Home Loan EMI Calculator helps you calculate EMI of your housing loan required to pay for your dream home. Know about Online Home Loan EMI Calculator here! | TheFinmaster'/>
        <meta name='keywords' content='hdfc home loan calculator, home loan emi calculator, sbi home loan interest rate calculator, axis bank home loan calculator, lic home loan emi calculator, axis bank home loan emi calculator, icici bank housing loan calculator' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/home-loan-emi-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Home Loan EMI Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className='py-4 '>

   
    <div className="container mx-auto p-4 shadow-xl rounded-md ">
      <h1 className="text-2xl font-bold mb-4">Home Loan EMI Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Amount</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={loanAmount}
                onChange={handleLoanAmountChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {loanAmount.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="100000000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (P.A.)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={interestRate}
                onChange={handleInterestRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{interestRate} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="15"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Tenure (Years)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="30"
                value={loanTenure}
                onChange={handleLoanTenureChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{loanTenure} Yr</span>
            </div>
            <input
              type="range"
              min="0"
              max="30"
              value={loanTenure}
              onChange={(e) => setLoanTenure(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateEMI}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
            <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
            <p>Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}</p>
            <p>Total Amount (Principal + Interest): ₹ {parseFloat(totalAmount).toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
        {amortizationSchedule.length > 0 && (
          <div className="flex flex-col space-y-4">
            {Array.from(new Set(amortizationSchedule.map(item => item.year))).map((year, idx) => (
              <div key={idx} className="mb-4 border rounded-md shadow-md">
                <div
                  className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${activeYear === year ? 'bg-blue-100' : ''}`}
                  onClick={() => handleYearToggle(year)}
                >
                  <span>{year}</span>
                  <span>{activeYear === year ? '-' : '+'}</span>
                </div>
                {activeYear === year && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-md shadow-md text-center">
                      <thead>
                        <tr>
                          <th className="py-2 px-4 border-b">Month</th>
                          <th className="py-2 px-4 border-b">Principal Paid</th>
                          <th className="py-2 px-4 border-b">Interest Charged</th>
                          <th className="py-2 px-4 border-b">Total Payment</th>
                          <th className="py-2 px-4 border-b">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amortizationSchedule.filter(item => item.year === year).map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-2 px-4 border-b">{new Date(0, item.month - 1).toLocaleString('default', { month: 'short' })}</td>
                            <td className="py-2 px-4 border-b">₹ {item.principalPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.interestPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.totalPayment.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.balance.toFixed(2).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
     </div>
     <div className="p-4 sm:p-8 bg-white">
      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
      Home Loan EMI Calculator 
      </h2>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {calculators.map((calc, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.04 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <Link
              to={calc.path}
              className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-3 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[140px]"
            >
              <img
                src={calc.image}
                alt={calc.name}
                className="w-10 h-10 object-contain mb-2"
              />
              <span className="text-md font-medium text-gray-800 break-words leading-snug">
                {calc.name}
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
     <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
     <div className="w-full md:w-2/3 p-4 mx-auto">
     <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Plan Your Dream Home with TheFinMaster’s Home Loan EMI Calculator
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Buying a home is one of the most significant financial decisions in a person’s life. With rising real estate prices and evolving loan options, careful planning becomes essential. That’s where TheFinMaster’s Home Loan EMI Calculator steps in—helping you visualize your monthly commitments, plan better, and move one step closer to your dream home.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Home Loan EMI?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              EMI, or Equated Monthly Instalment, is the fixed amount you repay every month towards your home loan. It includes both the principal (the borrowed amount) and the interest (charged by the lender). The EMI remains constant throughout the tenure, but the composition of interest and principal changes over time—initial payments include more interest, while later ones cover more principal.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Understanding your EMI beforehand allows you to align your property choices with your financial capacity and long-term goals.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use a Home Loan EMI Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Eliminate Guesswork, Embrace Clarity</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Manually calculating EMI using the formula:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>EMI = [P × R × (1 + R)^N] / [(1 + R)^N – 1]</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>E = EMI amount</li>
              <li>P = Principal amount</li>
              <li>R = Rate of interest</li>
              <li>N = Loan tenure</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              ...can be confusing and error-prone. Our tool takes the formula and turns it into an intuitive digital experience.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>In-Depth Financial Planning</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Knowing your EMI beforehand empowers you to:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Set a realistic loan amount based on your income.</li>
              <li>Determine the ideal loan tenure that balances repayment ability and interest costs.</li>
              <li>Compare offers from different lenders by adjusting interest rates in real time.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Save Time, Make Smarter Choices</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Instead of multiple consultations and trial-and-error spreadsheets, you get accurate answers in seconds. You can test different combinations of tenure, amount, and interest rate to understand which fits best into your budget.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Visualize Long-Term Impact</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A small tweak in the loan term or rate can save lakhs over the loan duration. Our EMI calculator helps you identify the most cost-effective path.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How TheFinMaster’s Home Loan EMI Calculator Works
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the calculator is simple and requires just three inputs:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Loan Amount:</strong> The amount you wish to borrow for your home.</li>
              <li><strong>Interest Rate (%):</strong> The annual interest rate offered by the lender.</li>
              <li><strong>Loan Tenure:</strong> The repayment period (in months or years).</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Once entered, our calculator instantly displays your:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Monthly EMI</li>
              <li>Total interest payable</li>
              <li>Total repayment amount</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              If you take a loan of ₹1 crore at an interest rate of 12% for 15 years, your monthly EMI will be approximately ₹1,10,108. Over the full term, you will repay ₹1.98 crores, with around ₹98 lakhs paid as interest.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This breakdown enables you to better assess the total cost of the loan before making any commitment.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Understanding the Amortization Schedule
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Though the EMI stays constant, the ratio of principal and interest changes each month. This is called an amortization schedule. In the early years, a higher portion of the EMI goes towards interest. As the loan progresses, the interest component reduces and the principal repayment increases.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This is crucial to understand if you're planning for prepayment, refinancing, or selling the property mid-loan.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              TheFinMaster provides amortization charts as part of our advanced resources to help you:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Track your remaining loan balance at any point.</li>
              <li>Estimate interest saved through part-prepayments.</li>
              <li>Understand the financial benefit of reducing tenure vs EMI.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using a Home Loan EMI Calculator
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Financial Clarity:</strong> Know what you’re signing up for. Avoid surprises.</li>
              <li><strong>Better Loan Selection:</strong> Compare different banks and NBFCs with real-time rate inputs.</li>
              <li><strong>Negotiation Power:</strong> Enter loan discussions informed and confident.</li>
              <li><strong>Flexible Planning:</strong> Explore how down payments, tenure changes, or prepayments affect your cost.</li>
              <li><strong>Risk Management:</strong> Evaluate worst-case scenarios, like increased interest rates.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Optimize Your Loan Strategy with TheFinMaster
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Planning to buy a house isn’t just about EMI—it’s about total loan strategy. Use our calculator to:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Find the sweet spot between low EMIs and minimum interest outgo.</li>
              <li>Understand how a bigger down payment reduces your EMI drastically.</li>
              <li>Learn how changing your loan tenure from 20 to 15 years can save lakhs in interest.</li>
              <li>Plan for joint loans by calculating shared liability and tax benefits.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Tips to Manage Your Home Loan Better
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Prepay Smartly:</strong> Even small extra payments early in your loan can reduce the tenure significantly.</li>
              <li><strong>Don’t Overborrow:</strong> Stay within 40% of your monthly income for EMIs.</li>
              <li><strong>Choose the Right Tenure:</strong> Longer tenure = lower EMI but higher interest paid.</li>
              <li><strong>Compare Interest Types:</strong> Fixed vs floating can have long-term implications.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Ready to Calculate?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Use TheFinMaster’s Home Loan EMI Calculator to plan better, borrow smarter, and move confidently towards your future home. Our tools are free, user-friendly, and backed by financial logic—making your journey smoother and stress-free.
            </p>
          </div>

        
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
     </>
  );
};

export default HomeLoanEMICalculator;

// import React, { useState } from 'react';
// import {
//   Chart as ChartJS,
//   ArcElement,
//   Tooltip as ChartTooltip,
//   Legend,
// } from 'chart.js';
// import { Pie } from 'react-chartjs-2';

// // Register necessary components
// ChartJS.register(
//   ArcElement,
//   ChartTooltip,
//   Legend
// );

// const HomeLoanEMICalculator = () => {
//   const [loanAmount, setLoanAmount] = useState(5000000);
//   const [rateOfInterest, setRateOfInterest] = useState(8.5);
//   const [loanTenure, setLoanTenure] = useState(20);
//   const [monthlyEMI, setMonthlyEMI] = useState(0);
//   const [totalInterest, setTotalInterest] = useState(0);
//   const [totalAmount, setTotalAmount] = useState(0);
//   const [activeYear, setActiveYear] = useState(null);

//   const calculateEMI = () => {
//     const monthlyInterestRate = rateOfInterest / 12 / 100;
//     const numberOfPayments = loanTenure * 12;
//     const emi = (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
//     const totalPayment = emi * numberOfPayments;
//     const totalInterestPaid = totalPayment - loanAmount;

//     setMonthlyEMI(emi.toFixed(2));
//     setTotalInterest(totalInterestPaid.toFixed(2));
//     setTotalAmount(totalPayment.toFixed(2));
//   };

//   const handleLoanAmountChange = (e) => {
//     const value = e.target.value;
//     setLoanAmount(value === '' ? 0 : parseInt(value));
//   };

//   const handleRateOfInterestChange = (e) => {
//     const value = e.target.value;
//     setRateOfInterest(value === '' ? 0 : parseFloat(value));
//   };

//   const handleLoanTenureChange = (e) => {
//     const value = e.target.value;
//     setLoanTenure(value === '' ? 0 : parseInt(value));
//   };

//   const toggleYear = (year) => {
//     setActiveYear(activeYear === year ? null : year);
//   };

//   const data = {
//     labels: ['Principal Amount', 'Total Interest', 'Total Amount'],
//     datasets: [
//       {
//         label: 'Loan Breakdown',
//         data: [loanAmount, totalInterest, totalAmount],
//         backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
//       },
//     ],
//   };

//   return (
//     <div className="container mx-auto p-4">
//       <h1 className="text-2xl font-bold mb-4">Home Loan EMI Calculator</h1>
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//         <div>
//           <div className="mb-4">
//             <label className="block text-gray-700 font-semibold">Loan Amount</label>
//             <div className="flex items-center">
//               <input
//                 type="number"
//                 min="0"
//                 value={loanAmount}
//                 onChange={handleLoanAmountChange}
//                 className="w-1/2 p-2 border rounded mr-2"
//               />
//               <span className="text-gray-700">₹ {loanAmount.toLocaleString()}</span>
//             </div>
//             <input
//               type="range"
//               min="0"
//               max="100000000"
//               value={loanAmount}
//               onChange={(e) => setLoanAmount(parseInt(e.target.value))}
//               className="w-full mt-2"
//             />
//           </div>
//           <div className="mb-4">
//             <label className="block text-gray-700 font-semibold">Rate of Interest (p.a.)</label>
//             <div className="flex items-center">
//               <input
//                 type="number"
//                 min="0"
//                 max="15"
//                 step="0.1"
//                 value={rateOfInterest}
//                 onChange={handleRateOfInterestChange}
//                 className="w-1/2 p-2 border rounded mr-2"
//               />
//               <span className="text-gray-700">{rateOfInterest} %</span>
//             </div>
//             <input
//               type="range"
//               min="0"
//               max="15"
//               step="0.1"
//               value={rateOfInterest}
//               onChange={(e) => setRateOfInterest(parseFloat(e.target.value))}
//               className="w-full mt-2"
//             />
//           </div>
//           <div className="mb-4">
//             <label className="block text-gray-700 font-semibold">Loan Tenure (years)</label>
//             <div className="flex items-center">
//               <input
//                 type="number"
//                 min="1"
//                 max="30"
//                 value={loanTenure}
//                 onChange={handleLoanTenureChange}
//                 className="w-1/2 p-2 border rounded mr-2"
//               />
//               <span className="text-gray-700">{loanTenure} Yr</span>
//             </div>
//             <input
//               type="range"
//               min="1"
//               max="30"
//               value={loanTenure}
//               onChange={(e) => setLoanTenure(parseInt(e.target.value))}
//               className="w-full mt-2"
//             />
//           </div>
//           <button
//             onClick={calculateEMI}
//             className="bg-blue-500 text-white px-4 py-2 rounded"
//           >
//             Calculate
//           </button>
//         </div>
//         <div>
//           <div className="mb-4">
//             <h2 className="text-xl font-bold">Results</h2>
//             <p>Monthly EMI: ₹ {monthlyEMI}</p>
//             <p>Total Interest: ₹ {totalInterest}</p>
//             <p>Total Amount: ₹ {totalAmount}</p>
//           </div>
//           <div>
//             <h2 className="text-xl font-bold">Breakdown</h2>
//             <div className="flex justify-center">
//               <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
//             </div>
//           </div>
//         </div>
//       </div>
//       <div className="mt-6">
//         <h2 className="text-xl font-bold">Amortization Schedule</h2>
//         <div>
//           {Array.from({ length: loanTenure }, (_, i) => i + 1).map((year) => (
//             <div key={year}>
//               <div
//                 className="cursor-pointer text-gray-700 font-semibold my-2"
//                 onClick={() => toggleYear(year)}
//               >
//                 {activeYear === year ? '-' : '+'} {2023 + year}
//               </div>
//               {activeYear === year && (
//                 <div className="pl-4">
//                   <table className="w-full text-left border-collapse">
//                     <thead>
//                       <tr>
//                         <th className="p-2 border">Month</th>
//                         <th className="p-2 border">Principal Paid</th>
//                         <th className="p-2 border">Interest Charged</th>
//                         <th className="p-2 border">Total Payment</th>
//                         <th className="p-2 border">Balance</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {/* Example amortization data */}
//                       <tr>
//                         <td className="p-2 border">Jan</td>
//                         <td className="p-2 border">₹ 14,149</td>
//                         <td className="p-2 border">₹ 5,416</td>
//                         <td className="p-2 border">₹ 19,566</td>
//                         <td className="p-2 border">₹ 9,85,851</td>
//                       </tr>
//                       {/* Add more rows as needed */}
//                     </tbody>
//                   </table>
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default HomeLoanEMICalculator;

